import { useMutation, useQueryClient } from "@tanstack/react-query";
import { HTTPError } from "ky";
import { useApi } from "../client";
import keys from "./keys";
import { Area, PostAction, Status } from "./types";
import { toast } from "sonner";
import { ModuleCommandEnum } from "@/types/enums/module-command.enum";

export const BROADCAST_ADDRESS = 0xffffffff;

export type HTPPErrorResponse = Error & { response: Response };

export type CommandToSend =
  | ModuleCommandEnum.FOLD
  | ModuleCommandEnum.UNFOLD
  | ModuleCommandEnum.PAUSE
  | ModuleCommandEnum.RESUME
  | ModuleCommandEnum.CANCEL
  | ModuleCommandEnum.GET_STATUS
  | ModuleCommandEnum.UP
  | ModuleCommandEnum.DOWN
  | ModuleCommandEnum.OPEN
  | ModuleCommandEnum.CLOSE;

export const useSendCommand = ({ areaId }: { areaId: string }) => {
  const api = useApi();
  const queryClient = useQueryClient();
  const queryKey = keys.all();

  return useMutation({
    mutationFn: async ({ command }: { command: CommandToSend }) => {
      const response = await api.post(`module-interactions/send-command`, {
        json: { areaId, command, destinationAddress: BROADCAST_ADDRESS },
      });

      return response.json();
    },
    mutationKey: queryKey,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKey });
    },
    onError: async (error) => {
      // toast.error("error");
      if (error instanceof HTTPError && error.response.status === 409) {
        await queryClient.invalidateQueries({ queryKey: queryKey });
      }
    },
  });
};

export const useLockArea = ({ areaId }: { areaId: string }) => {
  const api = useApi();
  const queryClient = useQueryClient();
  const queryKey = keys.one(areaId);

  return useMutation({
    mutationFn: async () => {
      const response = await api.patch(`areas/${areaId}/lock`);

      return response.json();
    },
    mutationKey: queryKey,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKey });
    },
    onError: async (error) => {
      // toast.error("error");
      if (error instanceof HTTPError && error.response.status === 409) {
        await queryClient.invalidateQueries({ queryKey: queryKey });
      }
    },
  });
};

export const useUnlockArea = ({ areaId }: { areaId: string }) => {
  const api = useApi();
  const queryClient = useQueryClient();
  const queryKey = keys.one(areaId);

  return useMutation({
    mutationFn: async () => {
      const response = await api.patch(`areas/${areaId}/unlock`);

      return response.json();
    },
    mutationKey: queryKey,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKey });
    },
    onError: async (error) => {
      // toast.error("error");
      if (error instanceof HTTPError && error.response.status === 409) {
        await queryClient.invalidateQueries({ queryKey: queryKey });
      }
    },
  });
};

export const useActionOnPost = ({ areaId }: { areaId: string }) => {
  const api = useApi();
  const queryClient = useQueryClient();
  const queryKey = keys.all();

  return useMutation({
    mutationFn: async ({
      postName,
      action,
    }: {
      postName: string;
      action: PostAction;
    }) => {
      const response = await api.post(`module-interactions/send-command`, {
        json: { areaId, destinationAddress: Number(postName), command: action },
      });

      return response.json();
    },
    mutationKey: queryKey,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKey });
      // toast.success("success");
    },
    onError: async (error) => {
      /**
       * Receiving a "409 - Conflict" response means we tried to perform an action the post can not do
       * (i.e. we sent "DEPLOY" but the post is already deployed or pending deployment).
       * This means our local data is probably not in sync with the server. We want to invalidate the query and refetch.
       */
      if (error instanceof HTTPError && error.response.status === 409) {
        await queryClient.invalidateQueries({ queryKey: queryKey });
      }
      // toast.error("error");
    },
  });
};

export const useUpdatePostStatus = () => {
  const api = useApi();
  const queryClient = useQueryClient();
  const queryKey = keys.all();

  return useMutation({
    mutationFn: async ({
      postName,
      statusToChange,
      areaId,
    }: {
      areaId: string;
      postName: string;
      statusToChange: Status;
    }) => {
      const response = await api.patch(`areas/${areaId}/post`, {
        json: {
          postName: postName,
          statusToChange: statusToChange,
        },
      });

      return response.json();
    },
    mutationKey: queryKey,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKey });
      // toast.success("success");
    },
    onError: () => {
      // toast.error("error");
    },
  });
};

export const usePausePost = ({ areaId }: { areaId: string }) => {
  const api = useApi();
  const queryClient = useQueryClient();
  const queryKey = keys.all();

  return useMutation({
    mutationFn: async ({ postName }: { postName: string }) => {
      const response = await api.patch(`areas/${areaId}/post`, {
        json: { postName: postName, action: "PAUSE" },
      });
      return response.json();
    },
    mutationKey: queryKey,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKey });
      // toast.success("success");
    },
    onError: () => {
      // toast.error("error");
    },
  });
};

export const useProblemOnPost = ({ areaId }: { areaId: string }) => {
  const api = useApi();
  const queryClient = useQueryClient();
  const queryKey = keys.all();
  // const { t } = useTranslation();

  return useMutation({
    mutationFn: async ({
      postName,
      problem,
    }: {
      postName: string;
      problem: string;
    }) => {
      const response = await api.patch(`areas/${areaId}/post`, {
        json: {
          postName: postName,
          problem: problem,
          statusToChange: "problem",
        },
      });
      return response.json();
    },
    mutationKey: queryKey,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKey });
      // toast.success("success");
    },
    onError: () => {
      // toast.error("error");
    },
  });
};

export const useActionOnArea = ({ areaId }: { areaId: string }) => {
  const api = useApi();
  const queryClient = useQueryClient();
  const queryKey = keys.all();

  return useMutation({
    mutationFn: async ({ action }: { action: PostAction }) => {
      const response = await api.post(`module-interactions/send-command`, {
        json: {
          areaId,
          command: action,
          destinationAddress: BROADCAST_ADDRESS,
        },
      });
      return response.json();
    },
    mutationKey: queryKey,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKey });
      // toast.success("success");
    },
    onError: () => {
      // toast.error("error");
    },
  });
};

export const usePostStatusReset = (areaId: Area["_id"]) => {
  const api = useApi();
  const queryClient = useQueryClient();
  const queryKey = keys.all();
  return useMutation({
    mutationFn: async ({ postName }: { postName: string }) => {
      const response = await api.patch(`areas/${areaId}/post`, {
        json: { postName: postName },
      });
      return response.json();
    },
    mutationKey: queryKey,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKey });
      // toast.success("success");
    },
    onError: () => {
      // toast.error("error");
    },
  });
};

export const useUpdatePhenologicalStage = ({
  areaId,
  onClose,
}: {
  areaId: string;
  onClose: () => void;
}) => {
  const api = useApi();
  const queryClient = useQueryClient();
  const queryKey = keys.one(areaId);

  return useMutation({
    mutationFn: async () => {
      const requestOptions = {
        headers: { "Content-Type": "application/json" },
      };
      const response = await api.patch(
        `areas/${areaId}/phenologicalStage`,
        requestOptions
      );

      return response.json();
    },
    mutationKey: queryKey,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKey });
      onClose();
      // toast.success("success");
    },
    onError: async (e: HTPPErrorResponse) => {
      const clonedResponse = e.response?.clone();
      const data = await clonedResponse?.json();

      toast.error(
        data.message ||
          "Nous n'avons pas pu mettre à jour le stade phénologique"
      );
    },
  });
};

// export const useUpdateAreaStatus = ({ areaId }: { areaId: string }) => {
//   const api = useApi();
//   const queryClient = useQueryClient();
//   const queryKey = keys.all();

//   return useMutation({
//     mutationFn: async ({ action }: { action: PostAction }) => {
//       const response = await api.patch(`areas/${areaId}`, {json: { action: action }});
//       return response.json();
//     },
//     mutationKey: queryKey,
//     onSuccess: async () => {
//       await queryClient.invalidateQueries({ queryKey: queryKey });
//       // toast.success("success");
//     },
//     onError: () => {
//       // toast.error("error");
//     },
//   });
// };
